import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { auth, db } from './firebase-config';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import Layout from './components/Layout';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import CreateTransaction from './pages/CreateTransaction';
import Transactions from './pages/Transactions';
import TransactionDetail from './pages/TransactionDetail';
import Settings from './pages/Settings';
import useSessionTimeout from './hooks/useSessionTimeout';

export default function App() {
  const [user, setUser] = useState(null);
  const [merchantData, setMerchantData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(null);

  // Auto-logout on inactivity (30 min)
  useSessionTimeout(!!user);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        try {
          // Verify user is a merchant
          const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
          if (userDoc.exists()) {
            const data = userDoc.data();
            if (data.userType === 'merchant') {
              setUser(currentUser);
              setMerchantData({ id: currentUser.uid, ...data });
              setAuthError(null);
            } else {
              setAuthError('This account is not registered as a merchant. Please contact support.');
              setUser(null);
              setMerchantData(null);
            }
          } else {
            setAuthError('Account not found. Please contact support.');
            setUser(null);
            setMerchantData(null);
          }
        } catch (err) {
          console.error('Auth check failed:', err);
          setAuthError('Failed to verify account. Please try again.');
          setUser(null);
          setMerchantData(null);
        }
      } else {
        setUser(null);
        setMerchantData(null);
        setAuthError(null);
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-text-secondary">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <Router>
      {!user ? (
        <Login error={authError} />
      ) : (
        <Layout user={user} merchantData={merchantData}>
          <Routes>
            <Route path="/" element={<Dashboard merchantData={merchantData} />} />
            <Route path="/create" element={<CreateTransaction merchantData={merchantData} />} />
            <Route path="/transactions" element={<Transactions merchantData={merchantData} />} />
            <Route path="/transactions/:id" element={<TransactionDetail merchantData={merchantData} />} />
            <Route path="/settings" element={<Settings merchantData={merchantData} />} />
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        </Layout>
      )}
    </Router>
  );
}
